/**
 *  @author: Maximilian Gertz
 *  @email: mgertz@nanocosmos.net
 *  @organization: Nanocosmos GmbH
 *  @version: (introduced in analytics-front) 2.26.0
 */

// React Imports
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { PropTypes } from 'prop-types';
import { Line } from 'react-chartjs-2';
import { Tooltip as ReactTooltip } from 'react-tooltip/dist/react-tooltip.umd';
import { CardTitle, Col, Row } from 'reactstrap';
import ReactDOMServer from 'react-dom/server';

// Redux Imports
import { useSelector, useDispatch } from 'react-redux';
import {
   alertFineStartSelector,
   alertFineEndSelector,
   troubleIntervalSelector,
   hashSelector,
   cancelTokenSelector,
   troubleModeSelector,
} from '../../../../../../redux/selectors/selectors';
import { setTroubleshootingAlertFineTimespan } from '../../../../../../redux/actions/general';
import { URL_PARAMS } from '../../../../../../urlSync/urlParamNames';

// External Libraries
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { getStyle } from '@coreui/coreui-pro/dist/js/coreui-utilities';
import { colorToRgba } from '../../../../../../util/UtilFunctions';
import moment from 'moment';
import axios from 'axios';

// Custom Imports
import { ALERT_MAPPER, getPrimaryAlertCode } from './AlertDetailsMapper';
import { AlertChartEventsSwitch, withFilteredEventAnnotations } from './AlertChartEvents';
import { ChartExplanation, getThresholdLegendDatasets, withAxisLabelAndLegendHandling } from './AlertChartLegend';
import {
   ChartWrapper,
   SetChartHeight,
   AlignedIconContainer,
   HeightSetContainer,
   PaddingContainer,
   SimpleFlexItem,
   SimpleFlexRow,
   TwoSideFlexContainer,
   WidgetBodyContainer,
   WidgetContainer,
   WidgetHeaderContainer,
   CenteredMsgShowing,
   BottomMargin,
   StandardContainer,
   Description,
   ValueText,
   CenterContentContainer,
   FlexRow,
   FontWeightedText,
   HintInfo,
} from '../../../../../../styledComponents/styledComponents';
import {
   aggEventWithSuggestedMinChartOptions,
   getOptionsWithAnnotationsEvents,
} from '../../../../../../constants/chartjs';
import {
   CHRONOLOGICAL_DATE_TIME_FORMAT,
   CHRONOLOGICAL_DATE_TIME_FORMAT_DASHED,
   SEARCH_TYPE,
   MAX_DURATION_IN_MS,
} from '../../../constants';
import { Spinner, CsvExportFromApiButton } from '../../../../../common';
import { getHttpHeader } from '../../../../../../util/FetchTools';
import AlertDetailsPanel from './AlertDetailsPanel';
import { DetailTile, TileContainer } from '../../../Styles';
import TextWithToolTip from '../../../TextWithToolTip';
import { E2E } from '../../../../../../constants/general';
import DateTimeSlider from '../../../general/DateTimeSlider';
import { getIntervalStart, getIntervalEnd } from '../../../general/DataProcessing';
import {
   ALERT_DETECTION_INTERVAL_IN_MINUTES,
   ALERT_FINE_START_REDUCTION_IN_MINUTES,
   ALERT_FINE_END_EXTENSION_IN_MINUTES,
} from '../constants';

// named after the severity the band opens the alert with, which is the wording the alerting service and the
// severity column of the alert table use as well
const TOLERANCE_LEVEL = {
   MODERATE: 'moderate',
   CRITICAL: 'critical',
};

const STREAM_TIME_RATIO_TOLERANCE = {
   [TOLERANCE_LEVEL.MODERATE]: 0.07,
   [TOLERANCE_LEVEL.CRITICAL]: 0.14,
};

const COLOR = {
   IDEAL: '#34d746',
   MODERATE: '#ed7d0e',
   CRITICAL: '#e92b2b',
};

// The tolerance lines span the whole plot, so a hairline is enough to place them. The ideal ratio stays
// heavier: it is the reference the eye should catch first, the tolerances only frame it.
const TOLERANCE_LINE_WIDTH = 1;

// the level decides which datapoints a tile counts - matching on the display name would tie the counting to
// the wording
const STR_DETAILS = [
   {
      level: TOLERANCE_LEVEL.MODERATE,
      name: 'Moderate STR Occurrences',
      description:
         'Count of stream time ratio stats that were <= 0.93 (lower orange line) and > 0.86 (lower red line) or >= 1.07 (upper orange line) and < 1.14 (upper red line) within the displayed time range.',
   },
   {
      level: TOLERANCE_LEVEL.CRITICAL,
      name: 'Critical STR Occurrences',
      description:
         'Count of stream time ratio stats that were <= 0.86 (lower red line) or >= 1.14 (upper red line) within the displayed time range.',
   },
];

const Y_AXIS_LABEL = 'Stream time ratio';

// The threshold lines are drawn as annotations, which get no legend entry of their own, so they are described
// in the legend instead - see AlertChartLegend. Each tolerance is one entry rather than two, because the lines
// come in symmetric pairs above and below the ideal ratio and mean the same thing on either side.
const THRESHOLD_LEGEND = [
   {
      label: 'Ideal ratio (1.00)',
      color: COLOR.IDEAL,
      explanation:
         'Stream time and real time advance at the same pace. A small fluctuation around this line is expected and harmless.',
   },
   {
      label: `Moderate (± ${STREAM_TIME_RATIO_TOLERANCE[TOLERANCE_LEVEL.MODERATE]})`,
      color: COLOR.MODERATE,
      explanation:
         'Beyond this deviation the ingest no longer keeps up with real time. Applications relying on ultra low latency are affected first.',
   },
   {
      label: `Critical (± ${STREAM_TIME_RATIO_TOLERANCE[TOLERANCE_LEVEL.CRITICAL]})`,
      color: COLOR.CRITICAL,
      explanation:
         'Beyond this deviation the stream drifts far enough for every viewer to notice the added latency. This is what opens the critical alert.',
   },
];

const CHART_NOTE =
   'Each point is one measurement of the ratio between elapsed real time and elapsed stream time. Below 1 the ingest falls behind real time, above 1 it runs ahead - both directions are a deviation, hence the mirrored pairs of lines.';

function isStrValueBeyondTolerance(str, toleranceLevel) {
   const tolerance = STREAM_TIME_RATIO_TOLERANCE[toleranceLevel];
   return str >= 1 + tolerance || str <= 1 - tolerance;
}

function getPerformanceAnnotations(selectedAlert, isStreamFinishedAfterAlert) {
   const startAndEndIsSame = selectedAlert.first === selectedAlert.last;

   const annotations = [
      {
         type: 'line',
         mode: 'horizontal',
         scaleID: 'y-axis-0',
         value: 1,
         borderColor: COLOR.IDEAL,
         borderWidth: 2,
      },
      {
         type: 'line',
         mode: 'horizontal',
         scaleID: 'y-axis-0',
         value: 0.93,
         borderColor: COLOR.MODERATE,
         borderWidth: TOLERANCE_LINE_WIDTH,
      },
      {
         type: 'line',
         mode: 'horizontal',
         scaleID: 'y-axis-0',
         value: 1.07,
         borderColor: COLOR.MODERATE,
         borderWidth: TOLERANCE_LINE_WIDTH,
      },
      {
         type: 'line',
         mode: 'horizontal',
         scaleID: 'y-axis-0',
         value: 0.86,
         borderColor: COLOR.CRITICAL,
         borderWidth: TOLERANCE_LINE_WIDTH,
      },
      {
         type: 'line',
         mode: 'horizontal',
         scaleID: 'y-axis-0',
         value: 1.14,
         borderColor: COLOR.CRITICAL,
         borderWidth: TOLERANCE_LINE_WIDTH,
      },
      {
         type: 'line',
         mode: 'vertical',
         scaleID: 'x-axis-0',
         // the look-back the detection evaluates, so the marker follows a retuned window instead of a literal
         value: moment
            .utc(selectedAlert.first, CHRONOLOGICAL_DATE_TIME_FORMAT)
            .subtract(ALERT_DETECTION_INTERVAL_IN_MINUTES, 'minutes')
            .format(),
         borderColor: 'black',
         borderWidth: 1.5,
         label: {
            backgroundColor: 'rgba(0,0,0, 0.5)',
            content: 'Detection Start',
            enabled: true,
            position: 'bottom',
            yAdjust: 75,
         },
      },
      {
         type: 'line',
         mode: 'vertical',
         scaleID: 'x-axis-0',
         value: moment.utc(selectedAlert.first, CHRONOLOGICAL_DATE_TIME_FORMAT).format(),
         borderColor: 'black',
         borderWidth: 1.5,
         label: {
            backgroundColor: 'rgba(0,0,0, 0.5)',
            content: startAndEndIsSame ? 'Emitted and stopped Alert' : 'Emitted Alert',
            enabled: true,
            position: 'bottom',
            yAdjust: 51,
         },
      },
      {
         type: 'line',
         mode: 'vertical',
         scaleID: 'x-axis-0',
         value:
            selectedAlert.status === 'Terminated'
               ? moment.utc(selectedAlert.last, CHRONOLOGICAL_DATE_TIME_FORMAT).format()
               : undefined,
         borderColor: 'black',
         borderWidth: 1.5,
         label: {
            backgroundColor: 'rgba(0,0,0, 0.5)',
            content: isStreamFinishedAfterAlert ? 'Stream stopped' : 'Stream recovered',
            enabled: true,
            position: 'bottom',
            yAdjust: 3,
         },
      },
   ];

   if (!startAndEndIsSame) {
      annotations.push({
         type: 'line',
         mode: 'vertical',
         scaleID: 'x-axis-0',
         value:
            selectedAlert.status === 'Terminated'
               ? moment.utc(selectedAlert.last, CHRONOLOGICAL_DATE_TIME_FORMAT).format()
               : undefined,
         borderColor: 'black',
         borderWidth: 1.5,
         label: {
            backgroundColor: 'rgba(0,0,0, 0.5)',
            content: 'Alert stopped',
            enabled: true,
            position: 'bottom',
            yAdjust: 27,
         },
      });
   }

   return annotations;
}

function StrPerformanceWidget({ streamName, selectedAlert, height }) {
   const ALERT_CODE = getPrimaryAlertCode(selectedAlert.codes);
   // a code the frontend does not know yet must not blank the page, so the widget degrades to the chart
   // without the code specific texts
   const { title, alertInfo, metricPath, maxRtmpStatDisplayRangeInMS, isExtendedDatapointRequired } =
      ALERT_MAPPER[ALERT_CODE] || {};

   // --------------------------
   // ## React useDispatch hook (REDUX)
   // --------------------------

   const dispatch = useDispatch();

   // --------------------------
   // ## React useSelector hooks
   // --------------------------

   const hash = useSelector(hashSelector);
   const cancelToken = useSelector(cancelTokenSelector);

   const alertFineStart = useSelector(alertFineStartSelector);
   const alertFineStartMoment = moment.utc(alertFineStart, CHRONOLOGICAL_DATE_TIME_FORMAT);

   const alertFineEnd = useSelector(alertFineEndSelector);
   const alertFineEndMoment = moment.utc(alertFineEnd, CHRONOLOGICAL_DATE_TIME_FORMAT);

   const interval = useSelector(troubleIntervalSelector);
   const mode = useSelector(troubleModeSelector);

   // --------------------------
   // ## React useState hooks
   // --------------------------

   const [areEventsShown, setAreEventsShown] = useState(true);
   const [chartData, setChartData] = useState({ labels: [], datasets: [] });
   const [chartOptions, setChartOptions] = useState({});
   const [occurences, setOccurences] = useState(
      STR_DETAILS.map((strType) => {
         return { name: strType.name, count: 0 };
      }),
   );

   // --------------------------
   // ## React useMemo hooks
   // --------------------------

   const header = useMemo(() => getHttpHeader(hash), [hash]);

   // --------------------------
   // ## React useCallback hooks
   // --------------------------

   const setAlertFineInterval = useCallback(
      (fineInterval) => {
         dispatch(
            setTroubleshootingAlertFineTimespan(
               { alertFineStart: fineInterval.fineStart, alertFineEnd: fineInterval.fineEnd },
               {
                  [URL_PARAMS.ALERT_FINE_START]: fineInterval.fineStart,
                  [URL_PARAMS.ALERT_FINE_END]: fineInterval.fineEnd,
               },
            ),
         );
      },
      [dispatch],
   );

   // --------------------------
   // ## React useQuery hooks
   // --------------------------

   const diff = alertFineEndMoment.diff(alertFineStartMoment, 'milliseconds');
   const alertDurationCanBeDisplayed = diff < MAX_DURATION_IN_MS;
   const isQueryEnabled =
      metricPath !== undefined && alertFineStart !== '' && alertFineEnd !== '' && alertDurationCanBeDisplayed;

   const { isLoading, isFetching, data: rtmpStats } = useQuery({
      enabled: isQueryEnabled,
      // every value the request is built from belongs into the key, or a cached response outlives the range,
      // the metric or the stream it was fetched for - and the bare range would collide with any other query
      queryKey: ['alertStreamTimeRatio', streamName, metricPath, alertFineStart, alertFineEnd],
      queryFn: async () => {
         // no interval parameter: the raw route answers with the unaggregated datapoints the detection
         // evaluates, which is what the occurrence counts below have to be computed from
         // prettier-ignore
         const url = `/v2/troubleshooting/rtmp/${metricPath}/raw/timeseries?streams=${streamName}&from=${moment.utc(alertFineStart, CHRONOLOGICAL_DATE_TIME_FORMAT).format('YYYY-MM-DDTHH:mm:ss')}&to=${moment.utc(alertFineEnd, CHRONOLOGICAL_DATE_TIME_FORMAT).format('YYYY-MM-DDTHH:mm:ss')}${isExtendedDatapointRequired ? '&extendedDatapoint=true' : ''}`;
         return axios.get(url, { headers: header }).then((res) => res.data);
      },
      onError: (err) => toast.error(err),
      retry: false,
      refetchOnWindowFocus: false,
   });

   // --------------------------
   // ## React useEffect hooks
   // --------------------------

   useEffect(() => {
      function createLineChartData(chartData, interval) {
         const xStart = moment.utc(alertFineStart, CHRONOLOGICAL_DATE_TIME_FORMAT).valueOf();
         const xEnd = moment.utc(alertFineEnd, CHRONOLOGICAL_DATE_TIME_FORMAT).valueOf();
         const alertEndMoment = moment.utc(selectedAlert.last);
         const minuteStartOfAlertEndUnixSec = alertEndMoment.clone().startOf('minute').subtract(1, 'minute').unix();
         const connectionIds = selectedAlert.connectionsDetails.map((connectionObj) => connectionObj.connectionId);

         let alertLastConnectionId = '';
         let isStreamFinishedAfterAlert = false;
         rtmpStats.data.forEach((dp, idx) => {
            if (dp.timestamp > minuteStartOfAlertEndUnixSec) {
               if (!alertLastConnectionId) {
                  alertLastConnectionId = dp.connectionId;
               } else {
                  if (dp.connectionId !== alertLastConnectionId) {
                     if (chartData.length > idx) {
                        chartData[idx] = { x: chartData[idx].x };
                        if (!isStreamFinishedAfterAlert) {
                           isStreamFinishedAfterAlert = rtmpStats.data.length - 1 !== idx;
                        }
                     }
                  }
               }
            }
            if (dp.timestamp < minuteStartOfAlertEndUnixSec) {
               if (!connectionIds.includes(dp.connectionId)) {
                  chartData[idx] = { x: chartData[idx].x };
               }
            }
         });

         const options = withAxisLabelAndLegendHandling(
            getOptionsWithAnnotationsEvents(
               aggEventWithSuggestedMinChartOptions(xStart, xEnd, Y_AXIS_LABEL, interval.stepsInMilliSec / 1000, 0.5),
               getPerformanceAnnotations(selectedAlert, isStreamFinishedAfterAlert),
            ),
            { axisLabel: Y_AXIS_LABEL, formatValue: (value) => value, realDatasetCount: 1 },
         );
         const colors = [];
         // calculate individual data point color
         chartData.forEach((point) => {
            if (isStrValueBeyondTolerance(point.y, TOLERANCE_LEVEL.CRITICAL)) {
               colors.push(COLOR.CRITICAL);
            } else if (isStrValueBeyondTolerance(point.y, TOLERANCE_LEVEL.MODERATE)) {
               colors.push(COLOR.MODERATE);
            } else {
               colors.push(COLOR.IDEAL);
            }
         });

         const datasetsConfig = {
            label: Y_AXIS_LABEL,
            backgroundColor: colorToRgba(getStyle('--info'), 10),
            borderColor: getStyle('--info'),
            borderWidth: 1,
            pointRadius: 2,
            pointBackgroundColor: colors,
            data: chartData,
            fill: 'origin',
            showLine: true,
            cubicInterpolationMode: 'monotone',
         };

         return { dataset: datasetsConfig, options };
      }
      if (rtmpStats?.data?.length > 0) {
         const { dataset, options } = createLineChartData(
            rtmpStats.data.map((item) => ({ x: item.timestamp * 1000, y: item.streamTimeRatio })),
            interval,
         );
         setChartData({ labels: [], datasets: [dataset, ...getThresholdLegendDatasets(THRESHOLD_LEGEND)] });
         setChartOptions(options);
      } else {
         setChartData({ labels: [], datasets: [] });
      }
   }, [selectedAlert, title, rtmpStats, interval, alertFineStart, alertFineEnd]);

   // Kept apart from the data: the switch hides four markers, so folding it into the effect above would
   // rebuild the datasets - and with them the occurrence counts - on each click.
   const displayedChartOptions = useMemo(() => withFilteredEventAnnotations(chartOptions, areEventsShown), [
      chartOptions,
      areEventsShown,
   ]);

   useEffect(() => {
      const countsByLevel = { [TOLERANCE_LEVEL.MODERATE]: 0, [TOLERANCE_LEVEL.CRITICAL]: 0 };
      if (chartData.datasets.length > 0) {
         chartData.datasets[0].data.forEach((datapoint) => {
            if (isStrValueBeyondTolerance(datapoint.y, TOLERANCE_LEVEL.CRITICAL)) {
               countsByLevel[TOLERANCE_LEVEL.CRITICAL]++;
            } else if (isStrValueBeyondTolerance(datapoint.y, TOLERANCE_LEVEL.MODERATE)) {
               countsByLevel[TOLERANCE_LEVEL.MODERATE]++;
            }
         });
      }
      setOccurences(STR_DETAILS.map(({ level, name }) => ({ name, count: countsByLevel[level] })));
   }, [chartData]);

   // --------------------------
   // ## Constants / Variables / Functions
   // --------------------------

   // prettier-ignore
   const CSV_EXPORT_STR_URL = `/v2/troubleshooting/rtmp/${metricPath}/raw/timeseries?streams=${streamName}&from=${moment.utc(alertFineStart, CHRONOLOGICAL_DATE_TIME_FORMAT).format('YYYY-MM-DDTHH:mm:ss')}&to=${moment.utc(alertFineEnd, CHRONOLOGICAL_DATE_TIME_FORMAT).format('YYYY-MM-DDTHH:mm:ss')}`;
   // prettier-ignore
   const FILE_NAME = `${moment.utc().format(CHRONOLOGICAL_DATE_TIME_FORMAT_DASHED)}_alert_str_id-${selectedAlert.id}_from-${moment.utc(alertFineStart, CHRONOLOGICAL_DATE_TIME_FORMAT).format(CHRONOLOGICAL_DATE_TIME_FORMAT_DASHED)}_to-${moment.utc(alertFineEnd, CHRONOLOGICAL_DATE_TIME_FORMAT).format(CHRONOLOGICAL_DATE_TIME_FORMAT_DASHED)}`;

   const dataStillInTimeRange = moment
      .utc(selectedAlert.first, CHRONOLOGICAL_DATE_TIME_FORMAT)
      .isSameOrAfter(moment.utc().subtract(2, 'weeks'));

   const sliderRangeInMS = alertFineEndMoment.diff(alertFineStartMoment, 'milliseconds');
   const sliderRangeIsValid = sliderRangeInMS <= maxRtmpStatDisplayRangeInMS;

   const adjustedFineStart = moment
      .utc(selectedAlert.first, CHRONOLOGICAL_DATE_TIME_FORMAT)
      .subtract(ALERT_FINE_START_REDUCTION_IN_MINUTES, 'minutes')
      .format(CHRONOLOGICAL_DATE_TIME_FORMAT);

   const adjustedFineEnd = moment
      .utc(selectedAlert.last, CHRONOLOGICAL_DATE_TIME_FORMAT)
      .add(ALERT_FINE_END_EXTENSION_IN_MINUTES, 'minutes')
      .format(CHRONOLOGICAL_DATE_TIME_FORMAT);

   return (
      <>
         {alertFineStart !== '' && alertFineEnd !== '' && (
            <BottomMargin pxSize={15}>
               <DateTimeSlider
                  startDateString={getIntervalStart(adjustedFineStart, interval.value)}
                  endDateString={getIntervalEnd(adjustedFineEnd, interval.value)}
                  fineStartString={alertFineStart}
                  fineEndString={alertFineEnd}
                  setFineInterval={setAlertFineInterval}
                  interval={interval}
                  searchType={SEARCH_TYPE.ALERT}
                  maxTimespanInMS={maxRtmpStatDisplayRangeInMS}
               />
               <BottomMargin pxSize={15} />
               <FlexRow>
                  <CenterContentContainer>
                     <HintInfo>
                        <FontWeightedText fontWeight={600}>
                           Note, that the displayed time range additionally includes:
                        </FontWeightedText>
                        <ul>
                           <li>
                              <span
                                 style={{ fontWeight: 600 }}
                              >{`${ALERT_FINE_START_REDUCTION_IN_MINUTES} minutes before`}</span>{' '}
                              the initial alert detection.
                           </li>
                           <li>
                              up to{' '}
                              <span
                                 style={{ fontWeight: 600 }}
                              >{`${ALERT_FINE_END_EXTENSION_IN_MINUTES} minutes after`}</span>{' '}
                              the alert was resolved.
                           </li>
                        </ul>
                     </HintInfo>
                  </CenterContentContainer>
               </FlexRow>
            </BottomMargin>
         )}
         <PaddingContainer>
            <WidgetContainer $margin={'0px 0px 0px 0px'}>
               <WidgetHeaderContainer>
                  <HeightSetContainer $heightInPx={23}>
                     <TwoSideFlexContainer>
                        <SimpleFlexRow>
                           <SimpleFlexItem>
                              <CsvExportFromApiButton
                                 url={CSV_EXPORT_STR_URL}
                                 header={header}
                                 cancelToken={cancelToken}
                                 filename={FILE_NAME}
                              />
                           </SimpleFlexItem>
                           <SimpleFlexItem>
                              <AlertChartEventsSwitch
                                 areEventsShown={areEventsShown}
                                 setAreEventsShown={setAreEventsShown}
                              />
                           </SimpleFlexItem>
                        </SimpleFlexRow>
                        <SimpleFlexRow>
                           <SimpleFlexItem>
                              <AlignedIconContainer
                                 data-tooltip-id='STR'
                                 data-tooltip-html={ReactDOMServer.renderToStaticMarkup(
                                    <ChartExplanation
                                       alertInfo={alertInfo}
                                       chartNote={CHART_NOTE}
                                       thresholds={THRESHOLD_LEGEND}
                                    />,
                                 )}
                                 className='bi bi-info-circle ml-1'
                              ></AlignedIconContainer>
                              <ReactTooltip
                                 id='STR'
                                 place={'top'}
                                 className={'reduced-line-height bring-on-top'}
                                 backgroundColor={'rgba(0,0,0,0.9)'}
                                 textColor={'rgb(240,240,240)'}
                                 style={{ textAlign: 'center', maxWidth: '400px' }}
                                 clickable
                              />
                           </SimpleFlexItem>
                        </SimpleFlexRow>
                     </TwoSideFlexContainer>
                  </HeightSetContainer>
               </WidgetHeaderContainer>
               <WidgetBodyContainer>
                  <Row>
                     <Col>
                        <CardTitle className='mb-0'>{title}</CardTitle>
                        <div className='small text-muted'>{`From ${alertFineStart} to ${alertFineEnd} \u00A0\u00A0( UTC time )`}</div>
                     </Col>
                  </Row>
                  <BottomMargin pxSize={20}>
                     {!isFetching && chartData.datasets.length > 0 && sliderRangeIsValid && (
                        <StandardContainer $paddingPx={15}>
                           <TileContainer gapPx={20} isFullScreen={mode !== E2E}>
                              {STR_DETAILS.map(({ name, description }) => (
                                 <div key={name}>
                                    <DetailTile
                                       key={name}
                                       heightPx={60}
                                       gapPx={10}
                                       style={{ cursor: 'pointer' }}
                                       data-tooltip-id={name}
                                       data-tooltip-html={ReactDOMServer.renderToStaticMarkup(<div>{description}</div>)}
                                    >
                                       <Description marginLeftPx={0} style={{ cursor: 'pointer' }}>
                                          {name}
                                       </Description>
                                       <ValueText>
                                          <TextWithToolTip
                                             text={occurences.find((strType) => strType.name === name).count}
                                             idName={name}
                                          />
                                       </ValueText>
                                    </DetailTile>
                                    {description && (
                                       <ReactTooltip
                                          id={name}
                                          place={'top'}
                                          className={'reduced-line-height bring-on-top'}
                                          backgroundColor={'rgba(0,0,0,0.9)'}
                                          textColor={'rgb(240,240,240)'}
                                          style={{ textAlign: 'center', maxWidth: '400px' }}
                                          clickable
                                       />
                                    )}
                                 </div>
                              ))}
                           </TileContainer>
                        </StandardContainer>
                     )}
                     <ChartWrapper className='chart-wrapper' height={height} marginTopPx={10}>
                        <Spinner loading={(isLoading || isFetching) && isQueryEnabled} parentTopMarginPx={60}>
                           {!sliderRangeIsValid ? (
                              <CenteredMsgShowing height={height} marginTopPx={20}>
                                 {'Selected time range is too big!'}
                                 <>
                                    <br />
                                    {`Please reduce the selected range to ${
                                       maxRtmpStatDisplayRangeInMS / 1000 / 60 / 60
                                    } hours!`}
                                 </>
                              </CenteredMsgShowing>
                           ) : (
                              <>
                                 {chartData.datasets?.length === 0 ? (
                                    <CenteredMsgShowing height={height} marginTopPx={20}>
                                       <>
                                          {'No data available'}
                                          {!dataStillInTimeRange && (
                                             <>
                                                <br />
                                                {'Details are only available for 14 days!'}
                                             </>
                                          )}
                                       </>
                                    </CenteredMsgShowing>
                                 ) : (
                                    <SetChartHeight height={height}>
                                       <Line data={chartData} options={displayedChartOptions}></Line>
                                    </SetChartHeight>
                                 )}
                              </>
                           )}
                        </Spinner>
                     </ChartWrapper>
                  </BottomMargin>
                  <AlertDetailsPanel selectedAlert={selectedAlert} />
               </WidgetBodyContainer>
            </WidgetContainer>
         </PaddingContainer>
      </>
   );
}

StrPerformanceWidget.propTypes = {
   selectedAlert: PropTypes.object,
   streamName: PropTypes.string,
   height: PropTypes.number,
};

StrPerformanceWidget.defaultProps = {
   height: 300,
};

export default StrPerformanceWidget;
